<template>
  <div>
    <vx-card>
      <data-table
        :column-defs="columnDefs"
        :default-col-def="defaultColDef"
        :row-data="rowData"
        :total="total"
        :current-page="currentPage"
        :grid-options="gridOptions"
        :pagination-page-size="pageSize"
        pagination
        @update:current-page="onUpdateCurrentPage"
        @update:pagination-page-size="onUpdatePaginationPageSize"
      >
        <template #actions>
          <vs-button
            class="ml-4"
            @click="onClickDownload"
          >
            変更履歴
          </vs-button>
          <vs-button
            class="ml-4"
            @click="onClickOptimize"
          >
            最適化ルールの登録
          </vs-button>
          <vs-button
            class="ml-4"
            @click="onClickDelivery"
          >
            配信ペースルールの登録
          </vs-button>
          <vs-button
            class="ml-4"
            @click="onClickTarget"
          >
            ターゲット追加ルールの登録
          </vs-button>
          <vs-button
            class="ml-4"
            @click="onClickBudget"
          >
            日予算最適化
          </vs-button>
        </template>
      </data-table>
    </vx-card>
    <form-modal
      v-model="showOptimizeForm"
      :title="`最適化ルールの${bid.id ? '更新' : '登録'}`"
      :submit-button-text="bid.id ? '更新' : '登録'"
      class="ad-bid-modal"
      @click:submit="onClickOptimizeSubmit"
    >
      <template #content>
        <ValidationForm
          ref="bidOptimizeFormObserver"
        >
          <ad-bid-optimize-form
            :id="bid.id"
            v-model:portfolio-ids="bid.portfolioIds"
            v-model:negative-keyword="bid.negativeKeyword"
            v-model:frequency-ids="bid.frequencyIds"
            v-model:lookback="bid.lookback"
            v-model:up-rule="bid.upRule"
            v-model:down-rule="bid.downRule"
            class="mb-10"
          />
        </ValidationForm>
      </template>
    </form-modal>
    <form-modal
      v-model="showDeliveryForm"
      :title="`配信ペースルールの${bid.id ? '更新' : '登録'}`"
      :submit-button-text="bid.id ? '更新' : '登録'"
      class="ad-bid-modal"
      @click:submit="onClickDeliverySubmit"
    >
      <template #content>
        <ValidationForm
          ref="bidDeliveryFormObserver"
        >
          <ad-bid-delivery-form
            :id="bid.id"
            v-model:portfolio-ids="bid.portfolioIds"
            v-model:negative-keyword="bid.negativeKeyword"
            v-model:frequency-ids="bid.frequencyIds"
            v-model:lookback="bid.lookback"
            v-model:up-rule="bid.upRule"
            v-model:down-rule="bid.downRule"
            class="mb-10"
          />
        </ValidationForm>
      </template>
    </form-modal>
    <form-modal
      v-model="showTargetForm"
      :title="`ターゲット追加ルールの${bid.id ? '更新' : '登録'}`"
      :submit-button-text="bid.id ? '更新' : '登録'"
      class="ad-bid-modal"
      @click:submit="onClickTargetSubmit"
    >
      <template #content>
        <ValidationForm
          ref="bidTargetFormObserver"
        >
          <ad-bid-target-form
            :id="bid.id"
            v-model:portfolio-ids="bid.portfolioIds"
            v-model:negative-keyword="bid.negativeKeyword"
            v-model:frequency-ids="bid.frequencyIds"
            v-model:lookback="bid.lookback"
            v-model:up-rule="bid.upRule"
            class="mb-10"
          />
        </ValidationForm>
      </template>
    </form-modal>
    <form-modal
      v-model="showBudgetForm"
      :title="`日予算最適化の${bid.id ? '更新' : '登録'}`"
      :submit-button-text="bid.id ? '更新' : '登録'"
      class="ad-bid-modal"
      @click:submit="onClickBudgetSubmit"
    >
      <template #content>
        <ValidationForm
          ref="bidBudgetFormObserver"
        >
          <ad-bid-budget-form
            :id="bid.id"
            v-model:portfolio-ids="bid.portfolioIds"
            v-model:frequency-ids="bid.frequencyIds"
            v-model:budget-rule="bid.budgetRule"
            class="mb-10"
          />
        </ValidationForm>
      </template>
    </form-modal>
    <confirm-modal
      v-model="showConfirmModal"
      title="ルール削除"
      @click:confirm="onClickConfirm"
      @click:cancel="showConfirmModal = false"
    >
      <template #content>
        <p class="p-4 whitespace-pre-wrap">
          ルールID {{ `${bid.id}` }} を本当に削除しますか？
        </p>
      </template>
    </confirm-modal>
  </div>
</template>

<script>
import { mapActions, mapState } from 'vuex'
import DataTable from '@/components/table/DataTable'
import DataTableCellActions from '@/components/table/DataTableCellActions'
import DataTableCellSwitch from '@/components/table/DataTableCellSwitch'
import FormModal from '@/components/modals/FormModal'
import ConfirmModal from '@/components/modals/ConfirmModal'
import AdBidOptimizeForm from '@/components/forms/AdBidOptimizeForm'
import AdBidDeliveryForm from '@/components/forms/AdBidDeliveryForm'
import AdBidTargetForm from '@/components/forms/AdBidTargetForm'
import AdBidBudgetForm from '@/components/forms/AdBidBudgetForm'
import dayOfWeek from '@/constants/dayOfWeek'
import bidRuleType from '@/constants/bidRuleType'
import { debounce } from '@/utils'

export default {
  name: 'AdBid',
  components: {
    DataTable,
    FormModal,
    ConfirmModal,
    AdBidOptimizeForm,
    AdBidDeliveryForm,
    AdBidTargetForm,
    AdBidBudgetForm
  },
  data () {
    return {
      query: null,
      sortParam: '',
      currentPage: 1,
      total: 0,
      pageSize: 20,
      defaultColDef: {
        sortable: false,
        editable: false,
        resizable: false,
        suppressMenu: true
      },
      columnDefs: [
        {
          headerName: 'アクション',
          cellRenderer: 'dataTableCellActions',
          cellRendererParams : {
            editable: true,
            deletable: true,
            onClickEdit : this.onClickEdit,
            onClickTrash: this.onClickTrash
          },
          width: 120
        },
        {
          headerName: 'ステータス',
          field: 'enabled',
          cellRenderer: 'dataTableCellSwitch',
          cellRendererParams : {
            action : this.onSwitchChange.bind(this)
          },
          width: 130
        },
        {
          headerName: 'ルールID',
          field: 'id',
          width: 120
        },
        {
          headerName: 'ルールタイプ',
          field: 'ruleType',
          valueFormatter: this.innerRuleType,
          width: 150
        },
        {
          headerName: '対象ポートフォリオ',
          field: 'portfolios',
          valueFormatter: this.innerPortfolioName
        },
        {
          headerName: 'キャンペーン名除外キーワード',
          field: 'negativeKeyword',
          width: 250
        },
        {
          headerName: '頻度',
          field: 'frequency',
          valueFormatter: this.innerFrequency,
          width: 150
        },
        {
          headerName: 'ルックバック期間',
          field: 'lookback',
          valueFormatter: this.innerLookback,
          width: 180
        }
      ],
      gridOptions: {},
      showOptimizeForm: false,
      showDeliveryForm: false,
      showTargetForm: false,
      showBudgetForm: false,
      showConfirmModal: false,
      bid: {
        id: null,
        portfolioIds: [],
        negativeKeyword: null,
        frequencyIds: [],
        lookback: null,
        upRule: {
          click: null,
          roas: {
            type: 1,
            value: null
          },
          bid_change: null,
          max_bid: null,
          usage_pace: null
        },
        downRule: {
          click: null,
          roas: {
            type: 1,
            value: null
          },
          bid_change: null,
          usage_pace: null
        },
        budgetRule: null
      }
    }
  },
  computed: {
    ...mapState('adBid', ['bids', 'portfolios']),
    ...mapState('account', ['activeAccount']),
    rowData: {
      get () {
        return JSON.parse(JSON.stringify(this.bids))
      }
    }
  },
  beforeMount () {
    this.gridOptions = {
      autoSizeStrategy: {
        type: 'fitCellContents'
      },
      components: {
        dataTableCellActions: DataTableCellActions,
        dataTableCellSwitch: DataTableCellSwitch
      }
    }
  },
  async mounted () {
    await this.listPortfolios()
  },
  unmounted () {
    this.destroyBids()
  },
  methods: {
    ...mapActions('adBid', [
      'listPortfolios',
      'listBids',
      'getBid',
      'addBid',
      'updateBid',
      'statusBid',
      'deleteBid',
      'destroyBids',
      'downloadCSV'
    ]),
    innerPortfolioName (params) {
      const name = []
      for (const id of params.value.split(',')) {
        name.push(this.portfolios[id].name)
      }
      return name.join(',')
    },
    innerRuleType (params) {
      if (params.value === bidRuleType.Optimize) {
        return '最適化'
      } else if (params.value === bidRuleType.Delivery) {
        return '配信ペース'
      } else if (params.value === bidRuleType.Target) {
        return 'ターゲット追加'
      } else if (params.value === bidRuleType.Budget) {
        return '日予算最適化'
      }
      return ''
    },
    innerFrequency (params) {
      const frequency = []
      for (const week of params.value.split(',')) {
        frequency.push(dayOfWeek[week])
      }
      return frequency.join(',')
    },
    innerLookback (params) {
      return `${params.value}日間`
    },
    fetchData: debounce(async (self) => {
      try {
        const payload = {
          page: self.currentPage,
          per_page: self.pageSize,
          sort: self.sortParam,
          q: self.query
        }
        const res = await self.listBids(payload)
        self.total = res.data.total
      } catch (e) {
        console.log(e)
      }
    }, 500),
    onUpdateCurrentPage (val) {
      this.currentPage = val
      this.fetchData(this)
    },
    onUpdatePaginationPageSize (val) {
      this.currentPage = 1
      this.pageSize = val
      this.fetchData(this)
    },
    async onClickDownload () {
      try {
        await this.downloadCSV()
      } catch (e) {
        console.log(e)
      }
    },
    async onClickOptimize () {
      await this.initializeBid()
      this.bid.ruleType = bidRuleType.Optimize
      this.showOptimizeForm = true
    },
    async onClickDelivery () {
      await this.initializeBid()
      this.bid.ruleType = bidRuleType.Delivery
      this.bid.frequencyIds = [1, 3, 5]
      this.bid.upRule.usage_pace = 95
      this.bid.downRule.bid_change = 15
      this.bid.downRule.usage_pace = 102
      this.showDeliveryForm = true
    },
    async onClickTarget () {
      await this.initializeBid()
      this.bid.ruleType = bidRuleType.Target
      this.bid.lookback = 7
      this.showTargetForm = true
    },
    async onClickBudget () {
      await this.initializeBid()
      this.bid.ruleType = bidRuleType.Budget
      this.bid.frequencyIds = [0, 1, 2, 3, 4, 5, 6]
      this.bid.budgetRule = {
        days5: 100,
        days10: 120,
        days20: 140,
        days31: 160,
        monday: 100,
        tuesday: 100,
        wednesday: 100,
        thursday: 100,
        friday: 100,
        saturday: 100,
        sunday: 100,
        min_budget: 1000
      }
      this.showBudgetForm = true
    },
    async onClickEdit (e) {
      await this.initializeBid()
      const res = await this.getBid({
        id: this.bids[e.index].id
      })
      const bid = res.data.data
      const portfolioIds = bid.portfolios.split(',').reduce((dist, id) => {
        return dist.concat({ text: this.portfolios[id].name, value: id })
      }, [])
      this.bid = {
        id: bid.id,
        ruleType: bid.ruleType,
        portfolioIds,
        negativeKeyword: bid.negativeKeyword,
        frequencyIds: bid.frequency.split(',').map(f => Number(f)),
        lookback: bid.lookback,
        upRule: bid.upRule,
        downRule: bid.downRule,
        budgetRule: bid.budgetRule,
        enabled: bid.enabled
      }
      if (bid.ruleType === bidRuleType.Optimize) {
        this.showOptimizeForm = true
      } else if (bid.ruleType === bidRuleType.Delivery) {
        this.showDeliveryForm = true
      } else if (bid.ruleType === bidRuleType.Target) {
        this.showTargetForm = true
      } else if (bid.ruleType === bidRuleType.Budget) {
        // 曜日実装前のデータには初期値を設定
        if (this.bid.budgetRule) {
          if (this.bid.budgetRule.monday == null) this.bid.budgetRule.monday = 100
          if (this.bid.budgetRule.tuesday == null) this.bid.budgetRule.tuesday = 100
          if (this.bid.budgetRule.wednesday == null) this.bid.budgetRule.wednesday = 100
          if (this.bid.budgetRule.thursday == null) this.bid.budgetRule.thursday = 100
          if (this.bid.budgetRule.friday == null) this.bid.budgetRule.friday = 100
          if (this.bid.budgetRule.saturday == null) this.bid.budgetRule.saturday = 100
          if (this.bid.budgetRule.sunday == null) this.bid.budgetRule.sunday = 100
        }
        this.showBudgetForm = true
      }
    },
    async onClickTrash (e) {
      const bid = this.bids[e.index]
      this.bid.id = bid.id
      this.showConfirmModal = true
    },
    async onClickConfirm () {
      await this.deleteBid({
        id: this.bid.id
      })
      this.showConfirmModal = false
      this.fetchData(this)
    },
    async onClickOptimizeSubmit () {
      try {
        const result = await this.$refs.bidOptimizeFormObserver.validate()
        if (!result.valid) return
        const data = {
          ...this.bid,
          portfolioIds: this.bid.portfolioIds.map(p => p.value)
        }
        if (data.id) {
          await this.updateBid(data)
        } else {
          await this.addBid(data)
        }
        this.showOptimizeForm = false
        // ちょっと遅らせてリセットしてあげないと、モーダルが閉じるまでにフォームの値が初期化されてしまって違和感がでる
        window.setTimeout(async () => {
          await this.initializeBid()
        }, 1000)
        this.fetchData(this)
      } catch (e) {
        console.log(e)
      }
    },
    async onClickDeliverySubmit () {
      try {
        const result = await this.$refs.bidDeliveryFormObserver.validate()
        if (!result.valid) return
        const data = {
          ...this.bid,
          portfolioIds: this.bid.portfolioIds.map(p => p.value)
        }
        if (data.upRule?.roas?.value == null && data.upRule?.click == null && data.upRule?.max_bid == null && data.upRule?.bid_change == null && data.upRule?.usage_pace == null) {
          // 配信ペースルールの入札強化は空にでも出来るようにする
          data.upRule = null;
        }
        if (data.id) {
          await this.updateBid(data)
        } else {
          await this.addBid(data)
        }
        this.showDeliveryForm = false
        // ちょっと遅らせてリセットしてあげないと、モーダルが閉じるまでにフォームの値が初期化されてしまって違和感がでる
        window.setTimeout(async () => {
          await this.initializeBid()
        }, 1000)
        this.fetchData(this)
      } catch (e) {
        console.log(e)
      }
    },
    async onClickTargetSubmit () {
      try {
        const result = await this.$refs.bidTargetFormObserver.validate()
        if (!result.valid) return
        const data = {
          ...this.bid,
          portfolioIds: this.bid.portfolioIds.map(p => p.value)
        }
        if (data.id) {
          await this.updateBid(data)
        } else {
          await this.addBid(data)
        }
        this.showTargetForm = false
        // ちょっと遅らせてリセットしてあげないと、モーダルが閉じるまでにフォームの値が初期化されてしまって違和感がでる
        window.setTimeout(async () => {
          await this.initializeBid()
        }, 1000)
        this.fetchData(this)
      } catch (e) {
        console.log(e)
      }
    },
    async onClickBudgetSubmit () {
      try {
        const result = await this.$refs.bidBudgetFormObserver.validate()
        if (!result.valid) return
        const data = {
          ...this.bid,
          portfolioIds: this.bid.portfolioIds.map(p => p.value)
        }
        if (data.id) {
          await this.updateBid(data)
        } else {
          await this.addBid(data)
        }
        this.showBudgetForm = false
        // ちょっと遅らせてリセットしてあげないと、モーダルが閉じるまでにフォームの値が初期化されてしまって違和感がでる
        window.setTimeout(async () => {
          await this.initializeBid()
        }, 1000)
        this.fetchData(this)
      } catch (e) {
        console.log(e)
      }
    },
    async onSwitchChange ({ index, value }) {
      await this.statusBid({
        id: this.bids[index].id,
        enabled: (value) ? 1 : 0
      })
      this.fetchData(this)
    },
    async initializeBid () {
      this.bid = {
        id: null,
        ruleType: null,
        portfolioIds: [],
        negativeKeyword: null,
        frequencyIds: [],
        lookback: null,
        upRule: {
          click: null,
          roas: {
            type: 1,
            value: null
          },
          bid_change: null,
          max_bid: null,
          usage_pace: null
        },
        downRule: {
          click: null,
          roas: {
            type: 1,
            value: null
          },
          bid_change: null,
          usage_pace: null
        },
        budgetRule: null,
        enabled: 1
      }
      await this.$refs.bidOptimizeFormObserver.resetForm()
      await this.$refs.bidDeliveryFormObserver.resetForm()
      await this.$refs.bidTargetFormObserver.resetForm()
      await this.$refs.bidBudgetFormObserver.resetForm()
    }
  }
}
</script>

<style scoped>
.ad-bid-modal :deep(.vs-popup) {
  width: 700px;
}
</style>
